import BackToTop from "~/components/footer/BackToTop";
import Logos, { Logo } from "~/components/footer/Logos";
import PaymentMethods from "~/components/footer/PaymentMethods";
import SafeStore from "~/components/footer/SafeStore";
import { PaymentIcon } from "~/components/footer/PaymentMethods";
import Social, { SocialMediaIcon } from "~/components/footer/Social";
import type { HTMLWidget, ImageWidget } from "~/types/widgets";
import { SafeStoreIcon } from "~/components/footer/SafeStore";
import CustomerService, {
  CustomerServiceElement,
} from "~/components/footer/CustomerService";
import WhatsApp from "~/components/ui/WhatsApp";
import CookieModal from "~/components/footer/CookieModal";
import LightBox from "~/components/footer/LightBox/LightBox";
import AppQRCode from "./AppQRCode";
export type Item = {
  /** @title Title */
  label: string;
  /** @title Link */
  href: string;
  /** @title Open in new tab */
  openInNewTab?: boolean;
};

export type FooterSection = {
  /** @title Title */
  label: string;
  /** @title Links */
  items: Item[];
};

export interface RegionOptions {
  currency?: Item[];
  language?: Item[];
}

export interface Layout {
  show?: {
    /** @title Pages */
    sectionLinks?: boolean;
    /** @title Customer Service */
    customerService?: boolean;
    /** @title Social Media */
    socialLinks?: boolean;
    /** @title Payment Methods */
    paymentMethods?: boolean;
    /** @title Safe Store Icons */
    safeStoreIcons?: boolean;
    /** @title Logos */
    logos?: boolean;
    /** @title Copyright Text */
    copyright?: boolean;
    /** @title Back to the top button */
    backToTheTop?: boolean;
    /** @title WhatsApp floating button */
    whatsApp?: boolean;
    /** @title Cookie Modal */
    cookieModal?: boolean;
    /** @title LightBox Modal */
    lightBox?: boolean;
    /** @title App QR Code */
    appQRCode?: boolean;
  };
}

export interface Props {
  layout?: Layout;
  /** @title Sections to be shown */
  sections?: FooterSection[];
  customerService?: {
    title: string;
    content: CustomerServiceElement[];
  };
  social?: {
    title?: string;
    socialMediaIcons?: SocialMediaIcon[];
  };
  payments?: {
    title?: string;
    paymentIcons?: PaymentIcon[];
  };
  safeStore?: {
    title?: string;
    safeStoreIcons?: SafeStoreIcon[];
  };
  logos?: Logo[];
  copyright?: HTMLWidget;
  backToTheTop?: {
    text?: string;
  };
  whatsApp?: {
    phone: string;
    message?: string;
  };
  /** @title LightBox Background*/
  lightBoxBackground?: {
    desktopImage: ImageWidget;
    mobileImage: ImageWidget;
  };
  /** @title App QR Code */
  appQRCode?: {
    title?: string;
    text?: string;
    qrCodeImage?: ImageWidget;
    qrCodeAlt?: string;
    appLink?: string;
  };
}

function Footer({
  layout,
  sections,
  customerService,
  social,
  payments,
  safeStore,
  logos,
  copyright,
  backToTheTop,
  whatsApp,
  lightBoxBackground,
  appQRCode,
}: Props) {
  const show = layout?.show;

  return (
    <footer className="w-full mt-15 mb-[45px]">
      <div className="w-full">
        <div className="flex flex-col w-full">
          {show?.sectionLinks && (
            <>
              {sections && sections.length > 0
                ? (
                  <>
                    <ul className="flex flex-col justify-between gap-8 xl:px-[150px] xl:flex-row">
                      {sections.map((section) => (
                        <>
                          <li className="hidden xl:block w-[215px] font-['Poppins'] not-italic font-normal text-sm leading-[14px] tracking-[0.06em]">
                            <h3 className="text-sm text-[#333333] mt-5 mb-2.5 font-bold uppercase">
                              {section.label}
                            </h3>
                            <ul className="flex flex-col">
                              {section.items?.map((item) => (
                                <li className="py-1.5">
                                  <a
                                    href={item.href}
                                    className="text-[#1A1A1A] text-sm font-normal"
                                    target={item.openInNewTab
                                      ? "_blank"
                                      : undefined}
                                    rel={item.openInNewTab
                                      ? "noopener noreferrer"
                                      : undefined}
                                  >
                                    {item.label}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </li>

                          <li className="xl:hidden font-['Poppins'] not-italic font-normal text-sm leading-[14px] tracking-[0.06em]">
                            <div className="collapse collapse-arrow border-b border-solid border-[#E4E4E4] rounded-none">
                              <input
                                type="checkbox"
                                aria-label="Expandir ou retrair"
                              />
                              <div className="collapse-title text-center w-[60%] my-0 mx-auto flex items-center justify-center p-0 uppercase text-[#333333] font-bold">
                                {section.label}
                              </div>
                              <div className="collapse-content text-center">
                                <ul>
                                  {section.items?.map((item) => (
                                    <li className="py-1.5">
                                      <a
                                        href={item.href}
                                        className="text-[#1A1A1A]"
                                        target={item.openInNewTab
                                          ? "_blank"
                                          : undefined}
                                        rel={item.openInNewTab
                                          ? "noopener noreferrer"
                                          : undefined}
                                      >
                                        {item.label}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </li>
                        </>
                      ))}

                      {show?.customerService && (
                        <li className="mx-auto my-0 text-center font-['Poppins'] not-italic font-normal text-sm leading-[14px] tracking-[0.06em] xl:m-0 xl:text-left">
                          <CustomerService {...customerService} />
                        </li>
                      )}

                      {show?.socialLinks && (
                        <li className="mx-auto my-0 w-[215px] lg:w-[254px] text-center font-['Poppins'] not-italic font-normal text-sm leading-[14px] tracking-[0.06em] uppercase xl:m-0 xl:text-left">
                          {show?.appQRCode && appQRCode && (
                            <div className="mb-8">
                              <AppQRCode {...appQRCode} />
                            </div>
                          )}
                          <Social {...social} />
                        </li>
                      )}
                    </ul>
                  </>
                )
                : <></>}
            </>
          )}
        </div>

        <div className="mt-15 mb-[35px] flex flex-col gap-[50px] justify-evenly items-center px-10 xl:flex-row xl:gap-0">
          {show?.paymentMethods && <PaymentMethods {...payments} />}
          {show?.safeStoreIcons && <SafeStore {...safeStore} />}
        </div>

        <div className="px-[150px]">{show?.logos && <Logos logos={logos} />}</div>

        {show?.copyright && copyright && (
          <div
            className="px-10 font-['Poppins'] not-italic font-normal text-[11px] leading-[12px] text-center text-[#333333] m-0 lg:px-[150px]"
            dangerouslySetInnerHTML={{ __html: copyright }}
          >
          </div>
        )}
      </div>
      {show?.backToTheTop && <BackToTop content={backToTheTop?.text} />}
      {show?.whatsApp && whatsApp && (
        <WhatsApp phone={whatsApp.phone} message={whatsApp.message} />
      )}
      {show?.lightBox && lightBoxBackground && (
        <LightBox {...lightBoxBackground} />
      )}
      {show?.cookieModal && <CookieModal />}
    </footer>
  );
}

export default Footer;
